import { useCallback, useContext } from "react";

import { PlusCircleIcon } from "@phosphor-icons/react";
import { useHistory } from "react-router";

import {
  Button,
  classes,
  classNames,
  Icon,
  SidebarState,
  UIContext,
} from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { useGetCurrentAccountIfLiveElseTradingAccount } from "~/v2/api/hooks";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { isPaperOrOnboarding } from "~/v2/utils";
import type { Asset } from "../../../api/rest";
import { BANKING_PATHS, ONBOARDING_PATHS, Path } from "../../../path";
import { AssetSearch } from "../../AssetSearch/AssetSearch.connector";
import { MarketStatusBadge } from "../../MarketStatusBadge/MarketStatusBadge.connector";

const BANKING_AND_ONBOARDING_PATHS = [...ONBOARDING_PATHS, ...BANKING_PATHS];

// a map of assets sorted by first letter and then word groups for fast searching
export type AssetMap = {
  // letter group
  [key: string]: {
    // word group
    [key: string]: Partial<Asset>;
  };
};

// normalize asset symbol and name for searching
export const normalizeAsset = (value: string) =>
  String(value)
    .toUpperCase()
    .trim()
    .replace(/[^a-z\s-]/gi, "");

export const createAssetMap = (assets: Map<string, string>) =>
  Array.from(assets.entries())
    .map(([symbol, name]) => ({ symbol, name }))
    .reduce((prev, next) => {
      const symbol = normalizeAsset(next.symbol);
      const name = normalizeAsset(next.name);
      const keyForSymbol = symbol[0];
      const keyForName = name[0];

      // create symbol character group
      if (!prev[keyForSymbol]) {
        prev[keyForSymbol] = {};
      }

      // create name character group
      if (!prev[keyForName]) {
        prev[keyForName] = {};
      }

      // add asset to symbol and name groups
      prev[keyForSymbol][symbol] = next;
      prev[keyForName][name] = next;

      return prev;
    }, {} as AssetMap);

export const TopBar = () => {
  const history = useHistory();

  const { sidebarState, setSidebarState } = useContext(UIContext);
  const { account } = useGetCurrentAccountIfLiveElseTradingAccount();

  const { product } = useAccountContext();

  // handle the deposit or open account button click
  const onDepositOrOpenAccountClick = useCallback(() => {
    // track the event
    AmplitudeProvider.dispatch(
      product === "paper"
        ? "navbar_open_live_account_button_clicked"
        : "navbar_deposit_funds_button_clicked",
    );

    // if the account is paper or onboarding, go to the new account page
    if (product === "paper") {
      history.push(Path.ROUTE_NEW_ACCOUNT);
    } else {
      // if the account is not paper or onboarding, go to the deposit page
      history.push(Path.ROUTE_FUNDING);
    }
  }, [history, product]);

  // handle state transition logic
  const handleSidebarState = (currentSidebarState: SidebarState) => {
    switch (currentSidebarState) {
      case SidebarState.HIDDEN:
        return SidebarState.MOBILE;
      case SidebarState.MOBILE:
        return SidebarState.HIDDEN;
      case SidebarState.EXPANDED:
        return SidebarState.COLLAPSED;
      case SidebarState.COLLAPSED:
        return SidebarState.EXPANDED;
      default:
        window.localStorage?.removeItem("sidebarState");
        return null;
    }
  };

  const isInBankingOrOnboardingPath = BANKING_AND_ONBOARDING_PATHS.some(
    (path) => window.location.pathname.includes(path),
  );

  const isDepositOrOpenAccountBtnDisplayed =
    // hide button while onboarding
    !isInBankingOrOnboardingPath &&
    (isPaperOrOnboarding(account) || product === "live") &&
    // hide button if live account cannot add funds
    !(
      product === "live" &&
      account?.status !== "ACTIVE" &&
      account?.status !== "ACCOUNT_UPDATED"
    );

  const isMarketStatusBadgeDisplayed = window.env.ENV !== "PROD";

  return (
    <div id="topbar" className="z-[2] flex w-full flex-col pr-2">
      <div
        className={classes(
          "z-5 mt-[1px] flex flex-row flex-wrap items-center gap-3 p-6 xs:pl-0 md:pr-6",
        )}
      >
        {sidebarState === SidebarState.HIDDEN && (
          <Button
            className="px-3"
            variant="secondary"
            onClick={() => {
              const newState = handleSidebarState(sidebarState);
              if (newState !== null) {
                setSidebarState(newState);
                window.localStorage?.setItem("sidebarState", newState);
              }
            }}
          >
            <Icon className="h-4 w-4" name="Bars3" />
          </Button>
        )}
        <div className="min-w-0 flex-1 lg:w-2/3 lg:flex-none">
          <AssetSearch />
        </div>
        {
          <div
            className={classNames(
              "xs:ml-auto flex w-full xs:w-auto shrink-0 items-center gap-x-4",
              isMarketStatusBadgeDisplayed && isDepositOrOpenAccountBtnDisplayed
                ? "justify-between"
                : "justify-end",
            )}
          >
            {isMarketStatusBadgeDisplayed && <MarketStatusBadge />}
            {isDepositOrOpenAccountBtnDisplayed && (
              <Button
                variant="v2/filled"
                onClick={onDepositOrOpenAccountClick}
                className="px-5 py-3"
              >
                {product === "paper" ? (
                  "Open Live Account"
                ) : (
                  <div className="flex flex-row items-center gap-2">
                    <PlusCircleIcon size={20} />
                    <span className="xs:hidden whitespace-nowrap sm:inline">
                      Add Funds
                    </span>
                  </div>
                )}
              </Button>
            )}
          </div>
        }
      </div>
    </div>
  );
};

export default TopBar;
